import { Localized, useLocalization } from "@fluent/react"
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import LockIcon from '@mui/icons-material/Lock'
import {
    Box, Button, FormControlLabel, Grid, IconButton, InputAdornment, ListItemIcon, ListItemText, MenuItem, Stack, Switch,
    TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material"
import { CopyToClipboardButton } from "../../../app/components/CopyToClipboardButton"
import { FormFieldDef, FormFieldOption, FormFieldType, L10nText, localizedText } from "../../documents/sections/formSection/formSchema"
import { FIELD_TYPE_ICONS, FIELD_TYPES } from "./FieldOutline"
import { DefinitionIssue, FieldLocks, hasOptions, isOptionLocked, placeholdersOf } from "./formDefinitionRules"

export const LockAdornment = () =>
    <InputAdornment position="end">
        <Tooltip title={<Localized id="form-def-locked-hint"><span>_Zablokowane po publikacji: szablony dokumentów mogą się do tego odwoływać.</span></Localized>}>
            <LockIcon fontSize="small" color="action" />
        </Tooltip>
    </InputAdornment>

export const IssueText = ({ issue }: { issue: DefinitionIssue }) =>
    <Localized id={issue.id} vars={issue.vars}><span>{issue.fallback}</span></Localized>

/** First issue of a target as helper text, so the problem shows next to its input. */
const issueHelper = (issues: DefinitionIssue[], target: DefinitionIssue['target']) => {
    const issue = issues.find(i => i.target === target)
    return issue ? { error: issue.severity === 'error', helperText: <IssueText issue={issue} /> } : {}
}

const LabelPair = ({ label, value, onChange, readOnly, issues, target }: {
    label: string
    value: L10nText | undefined
    onChange: (value: L10nText) => void
    readOnly: boolean
    issues?: DefinitionIssue[]
    target?: DefinitionIssue['target']
}) => {
    const current = value ?? { PL: '', EN: '' }
    const helper = issues && target ? issueHelper(issues, target) : {}
    // One message is enough for the pair: under PL when PL is empty, else under EN.
    const helperIndex = current.PL.trim() === '' ? 0 : 1
    return <Grid container spacing={1}>
        {(['PL', 'EN'] as const).map((language, i) =>
            <Grid item xs={12} sm={6} key={language}>
                <TextField fullWidth size="small" label={`${label} (${language})`}
                    value={current[language]}
                    onChange={e => onChange({ ...current, [language]: e.target.value })}
                    InputProps={{ readOnly }}
                    {...(i === helperIndex ? helper : {})}
                />
            </Grid>)}
    </Grid>
}

const OptionsEditor = ({ field, locks, readOnly, issues, onChange }: {
    field: FormFieldDef
    locks: FieldLocks
    readOnly: boolean
    issues: DefinitionIssue[]
    onChange: (options: FormFieldOption[]) => void
}) => {
    const { l10n } = useLocalization()
    const options = field.options ?? []
    const update = (index: number, patch: Partial<FormFieldOption>) =>
        onChange(options.map((o, i) => i === index ? { ...o, ...patch } : o))
    const optionIssues = issues.filter(i => i.target === 'options')

    return <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
            <Localized id="form-def-options"><span>_Opcje</span></Localized>
        </Typography>
        <Stack spacing={1}>
            {options.map((option, index) => {
                const valueLocked = isOptionLocked(locks, options, index)
                return <Box key={index} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '180px 1fr 1fr 40px' }, gap: 1, alignItems: 'center' }}>
                    <TextField size="small" label={l10n.getString('form-def-option-value', null, 'Wartość')}
                        value={option.value}
                        onChange={e => update(index, { value: e.target.value })}
                        InputProps={{ readOnly: readOnly || valueLocked, endAdornment: valueLocked ? <LockAdornment /> : undefined, sx: { fontFamily: 'monospace' } }}
                    />
                    <TextField size="small" label="PL" value={option.label.PL} InputProps={{ readOnly }}
                        onChange={e => update(index, { label: { ...option.label, PL: e.target.value } })} />
                    <TextField size="small" label="EN" value={option.label.EN} InputProps={{ readOnly }}
                        onChange={e => update(index, { label: { ...option.label, EN: e.target.value } })} />
                    {!readOnly && <IconButton size="small" onClick={() => onChange(options.filter((_, i) => i !== index))}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>}
                </Box>
            })}
        </Stack>
        {optionIssues.map((issue, i) =>
            <Typography key={i} variant="caption" display="block" color={issue.severity === 'error' ? 'error' : 'warning.dark'}>
                <IssueText issue={issue} />
            </Typography>)}
        {!readOnly && <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }}
            onClick={() => onChange([...options, { value: `OPTION_${options.length + 1}`, label: { PL: '', EN: '' } }])}>
            <Localized id="form-def-add-option"><span>_Dodaj opcję</span></Localized>
        </Button>}
    </Box>
}

export const FieldProperties = ({ field, code, locks, issues, readOnly, onChange, onTypeChange, onKeyChange, onRemove }: {
    field: FormFieldDef
    code: string
    locks: FieldLocks
    /** This field's issues only. */
    issues: DefinitionIssue[]
    readOnly: boolean
    onChange: (patch: Partial<FormFieldDef>) => void
    onTypeChange: (type: FormFieldType) => void
    onKeyChange: (key: string) => void
    onRemove: () => void
}) => {
    const { l10n } = useLocalization()
    const heading = field.type === 'heading'

    return <Stack spacing={2.5}>
        <Grid container spacing={1}>
            <Grid item xs={12} sm={5}>
                <TextField select fullWidth size="small" label={l10n.getString('form-def-field-type', null, 'Typ')}
                    value={field.type}
                    onChange={e => onTypeChange(e.target.value as FormFieldType)}
                    disabled={readOnly || locks.locked}
                    helperText={locks.locked
                        ? <Localized id="form-def-type-locked"><span>_Typ jest zablokowany po publikacji</span></Localized>
                        : undefined}
                    SelectProps={{ renderValue: type => l10n.getString(`form-field-type-${type}`, null, String(type)) }}
                >
                    {FIELD_TYPES.map(type => <MenuItem key={type} value={type}>
                        <ListItemIcon>{FIELD_TYPE_ICONS[type]}</ListItemIcon>
                        <ListItemText>{l10n.getString(`form-field-type-${type}`, null, type)}</ListItemText>
                    </MenuItem>)}
                </TextField>
            </Grid>
            <Grid item xs={12} sm={7}>
                <TextField fullWidth size="small" label={l10n.getString('form-def-key', null, 'Klucz')}
                    value={field.key}
                    onChange={e => onKeyChange(e.target.value)}
                    InputProps={{
                        readOnly: readOnly || locks.locked,
                        endAdornment: locks.locked ? <LockAdornment /> : undefined,
                        sx: { fontFamily: 'monospace' },
                    }}
                    {...issueHelper(issues, 'key')}
                />
            </Grid>
        </Grid>

        <LabelPair label={l10n.getString('form-def-label', null, 'Etykieta')} value={field.label} readOnly={readOnly}
            onChange={label => onChange({ label })} issues={issues} target="label" />

        {!heading && <>
            <LabelPair label={l10n.getString('form-def-help', null, 'Podpowiedź')} value={field.help} readOnly={readOnly}
                onChange={help => onChange({ help: help.PL === '' && help.EN === '' ? undefined : help })} />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3 }}>
                <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                        <Localized id="form-def-width"><span>_Szerokość</span></Localized>
                    </Typography>
                    <ToggleButtonGroup size="small" exclusive disabled={readOnly}
                        value={field.width ?? 12}
                        onChange={(_, width) => width && onChange({ width })}>
                        <ToggleButton value={12}><Localized id="form-def-width-full"><span>_Cała</span></Localized></ToggleButton>
                        <ToggleButton value={6}>1/2</ToggleButton>
                        <ToggleButton value={4}>1/3</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <FormControlLabel disabled={readOnly}
                    control={<Switch checked={field.required === true} onChange={e => onChange({ required: e.target.checked || undefined })} />}
                    label={<Localized id="form-def-required"><span>_Wymagane (tylko oznaczenie)</span></Localized>} />
            </Box>

            {hasOptions(field.type) && <OptionsEditor field={field} locks={locks} readOnly={readOnly} issues={issues}
                onChange={options => onChange({ options })} />}

            <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                    <Localized id="form-def-placeholder"><span>_Znacznik w szablonie dokumentu</span></Localized>
                </Typography>
                {placeholdersOf(code || '…', field).map(({ tag, kind, option }) =>
                    <Box key={tag} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Box component="code" sx={{ bgcolor: 'action.hover', px: 1, py: 0.5, borderRadius: 1, wordBreak: 'break-all' }}>
                            {tag}
                        </Box>
                        <CopyToClipboardButton value={tag} disabled={code === ''} />
                        <Typography variant="caption" color="text.secondary">
                            <Localized id={`form-def-tag-${kind}`} vars={{ option: option ? localizedText(option.label, 'MULTI') || option.value : '' }}>
                                <span>{kind}</span>
                            </Localized>
                        </Typography>
                    </Box>)}
            </Box>
        </>}

        {!readOnly && <Box>
            <Button color="error" variant="outlined" size="small" startIcon={<DeleteIcon />} onClick={onRemove}>
                <Localized id="form-def-remove-field"><span>_Usuń pole</span></Localized>
            </Button>
            {locks.locked && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                <Localized id="form-def-remove-reserved-hint"><span>_Klucz pozostanie zarezerwowany.</span></Localized>
            </Typography>}
        </Box>}
    </Stack>
}
